/**
 * Outil « États de la matière » (Boîte à outils, physique-chimie 6e → 2nde).
 *
 * Explorer :
 * - États : on chauffe (ou on refroidit) 100 g d'eau, de cyclohexane, d'acide stéarique ou d'eau
 *   salée ; la courbe de température se trace pendant le film, les particules (les mêmes dans
 *   les trois états) s'ordonnent ou s'agitent, le thermomètre suit ; masse, volume, énergie.
 * - Masse volumique : un cube d'un matériau posé dans un liquide (flotte ou coule), la balance,
 *   la droite m = ρ × V.
 * - Dissolution : sel ou sucre ajouté à 100 mL d'eau, jusqu'à saturation ; balance (masse
 *   conservée), solubilité selon la température.
 * Défi : une question par sorte selon le niveau. Logique : `matter.ts`.
 */

import { useMemo, useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { Note, ParamSlider, Segmented, Stepper } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { McqChallenge } from '../../ui/mcq-challenge';
import { PlayBar, usePlayer } from '../../ui/player';
import { RichText } from '../../ui/rich-text';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  LIQUIDS,
  MASS,
  MATERIALS,
  SOLUTES,
  SUBSTANCES,
  WATER_ML,
  buoyancy,
  changeOf,
  curve,
  densityLines,
  dissolve,
  dissolveLines,
  duration,
  energetic,
  fr,
  immersed,
  makeQuiz,
  minutes,
  partsFor,
  phaseCounts,
  quizKinds,
  rhoText,
  stateAt,
  stateLines,
  stateText,
  substanceById,
  tempText,
  volumeOf,
  volumeText,
  type Direction,
  type Part,
  type Substance,
} from './matter';

const W = 340;
const INK = 'var(--color-ink-700)';
const MUTED = 'var(--color-ink-500)';
const HOT = 'var(--color-error-500)';
const PARTICLE = 'var(--color-primary-500)';
const SALT = 'var(--color-warning-500)';

function Lines({ lines }: { lines: string[] }) {
  return (
    <Note>
      {lines.map((l) => (
        <span key={l} className="mb-1 block last:mb-0" data-testid="mt-law">
          <RichText>{l}</RichText>
        </span>
      ))}
    </Note>
  );
}

function Readout({ rows }: { rows: Array<[string, string]> }) {
  return (
    <table className="w-full text-sm" data-testid="mt-readings">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-ink-100 border-b last:border-0">
            <th scope="row" className="text-ink-600 py-1.5 pr-2 text-left font-normal">
              <RichText>{k}</RichText>
            </th>
            <td className="text-ink-900 py-1.5 text-right font-semibold tabular-nums">
              {frenchSpaces(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ---------- états ---------- */

const N = 30;
const BOX = { x0: 54, x1: 236, y0: 212, y1: 322 };
const SLOT = 15;
const COLS = 11;

/** positions fixes des places « condensées » (du bas vers le haut) */
function slot(k: number, disorder: boolean): [number, number] {
  const row = Math.floor(k / COLS);
  const col = k % COLS;
  const x = BOX.x0 + 14 + col * SLOT + (disorder && row % 2 ? SLOT / 2 : 0);
  const y = BOX.y1 - 9 - row * (SLOT - 1);
  return [x, y];
}

const phase = (i: number) => (i * 2.399) % (2 * Math.PI);
const bounce = (v: number, lo: number, hi: number) => {
  const span = hi - lo;
  const m = (((v - lo) % (2 * span)) + 2 * span) % (2 * span);
  return lo + (m <= span ? m : 2 * span - m);
};

function Particles({
  s,
  T,
  counts,
  tau,
}: {
  s: Substance;
  T: number;
  counts: { solid: number; liquid: number; gas: number };
  tau: number;
}) {
  const [t0, t1] = s.range;
  const heat = 0.4 + (T - t0) / (t1 - t0); // agitation
  const out: Array<{ x: number; y: number; k: string }> = [];
  for (let i = 0; i < counts.solid; i++) {
    const [x, y] = slot(i, false);
    const a = 0.9 * heat;
    out.push({
      x: x + a * Math.sin(25 * tau + phase(i)),
      y: y + a * Math.cos(31 * tau + phase(i)),
      k: 's',
    });
  }
  for (let j = 0; j < counts.liquid; j++) {
    const i = counts.solid + j;
    const [x, y] = slot(i, true);
    const a = 2.6 * heat;
    out.push({
      x: Math.min(
        BOX.x1 - 8,
        Math.max(BOX.x0 + 8, x + 3 * Math.sin(3.1 * j) + a * Math.sin(3 * tau + phase(i))),
      ),
      y: y + 2 * Math.cos(2.3 * j) + a * 0.7 * Math.cos(2.4 * tau + 2 * phase(i)),
      k: 'l',
    });
  }
  const condensedRows = Math.ceil((counts.solid + counts.liquid) / COLS);
  const top = BOX.y1 - 9 - condensedRows * (SLOT - 1);
  for (let j = 0; j < counts.gas; j++) {
    const v = 40 + 25 * heat;
    const ang = phase(j + 7) * 3;
    out.push({
      x: bounce(BOX.x0 + 10 + ((j * 37) % 160) + v * Math.cos(ang) * tau, BOX.x0 + 8, BOX.x1 - 8),
      y: bounce(
        BOX.y0 + 10 + ((j * 23) % 60) + v * Math.sin(ang) * tau,
        BOX.y0 + 8,
        Math.max(BOX.y0 + 20, top),
      ),
      k: 'g',
    });
  }
  // eau salée : quelques ions du sel, toujours dans le liquide
  const salt =
    s.id === 'brine'
      ? Array.from({ length: 4 }, (_, j) => {
          const [x, y] = slot(counts.solid + ((j * 5 + 2) % Math.max(1, counts.liquid)), true);
          return { x: x + 7 + 2 * Math.sin(3 * tau + j), y: y - 6 + 2 * Math.cos(2.6 * tau + j) };
        })
      : [];
  return (
    <g data-testid="mt-particles">
      <rect
        x={BOX.x0}
        y={BOX.y0}
        width={BOX.x1 - BOX.x0}
        height={BOX.y1 - BOX.y0}
        rx={8}
        fill="none"
        stroke={INK}
        strokeWidth={1.5}
      />
      {out.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r={6.5}
          fill={PARTICLE}
          opacity={p.k === 'g' ? 0.95 : 0.85}
          stroke="white"
          strokeWidth={1}
          data-testid={`mt-p-${p.k}`}
        />
      ))}
      {counts.liquid > 0 &&
        salt.map((p, i) => <circle key={`s${i}`} cx={p.x} cy={p.y} r={3.5} fill={SALT} />)}
    </g>
  );
}

function Thermometer({ T, range }: { T: number; range: readonly [number, number] }) {
  const x = 262;
  const top = BOX.y0 + 2;
  const bottom = BOX.y1 - 14;
  const level = bottom - ((T - range[0]) / (range[1] - range[0])) * (bottom - top);
  return (
    <g data-testid="mt-thermometer">
      <rect
        x={x - 5}
        y={top}
        width={10}
        height={bottom - top + 4}
        rx={5}
        fill="var(--color-ink-100)"
        stroke={INK}
      />
      <rect x={x - 2.5} y={level} width={5} height={bottom - level + 4} fill={HOT} />
      <circle cx={x} cy={bottom + 8} r={8} fill={HOT} stroke={INK} />
      <HaloText
        x={x + 14}
        y={Math.max(top + 10, Math.min(bottom, level + 4))}
        fontSize={11}
        fontWeight={700}
        fill={INK}
      >
        {tempText(T)}
      </HaloText>
    </g>
  );
}

function StatesFigure({ s, dir, t }: { s: Substance; dir: Direction; t: number }) {
  const D = duration(s);
  const pts = useMemo(() => curve(s, dir, 300), [s, dir]);
  const [t0, t1] = s.range;
  const L = 40;
  const R = W - 14;
  const TOP = 26;
  const B = 176;
  const X = (time: number) => L + (time / D) * (R - L);
  const Y = (T: number) => B - ((T - t0) / (t1 - t0)) * (B - TOP);
  const st = stateAt(s, dir, t);
  const drawn = pts.filter(([time]) => time <= t);
  const line = [...drawn, [t, st.T] as [number, number]];
  const tStep = t1 - t0 > 80 ? 20 : 10;
  const tTicks: number[] = [];
  for (let v = Math.ceil(t0 / tStep) * tStep; v <= t1; v += tStep) tTicks.push(v);
  const mStep = D / 60 > 12 ? 5 : 2;
  const mTicks: number[] = [];
  for (let m = 0; m * 60 <= D; m += mStep) mTicks.push(m);
  const reached = (T: number) => line.some(([, v]) => Math.abs(v - T) < 1e-6);
  const marks: Array<[number, string]> = [];
  if (s.pure) {
    if (reached(s.Tf))
      marks.push([s.Tf, `${dir === 'heat' ? 'fusion' : 'solidification'} ${fr(s.Tf, 1)} °C`]);
    if (s.Teb !== null && reached(s.Teb))
      marks.push([s.Teb, `${dir === 'heat' ? 'ébullition' : 'liquéfaction'} ${fr(s.Teb, 1)} °C`]);
  }
  const counts = phaseCounts(st, N);
  return (
    <svg
      viewBox={`0 0 ${W} 336`}
      className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
      role="img"
      aria-label={`${s.label} : ${stateText(st)} à ${tempText(st.T)}, après ${minutes(t)}`}
      data-testid="mt-figure"
    >
      <line x1={L} y1={B} x2={R} y2={B} stroke="var(--color-ink-300)" />
      <line x1={L} y1={TOP} x2={L} y2={B} stroke="var(--color-ink-300)" />
      {tTicks.map((v) => (
        <g key={v}>
          <line x1={L} y1={Y(v)} x2={R} y2={Y(v)} stroke="var(--color-ink-100)" />
          <text x={L - 5} y={Y(v) + 3.5} textAnchor="end" fontSize={9.5} fill={MUTED}>
            {fr(v, 0)}
          </text>
        </g>
      ))}
      <text x={L + 2} y={14} fontSize={10} fill={INK} fontWeight={700}>
        température (°C)
      </text>
      {mTicks.map((m) => (
        <text key={m} x={X(m * 60)} y={B + 12} textAnchor="middle" fontSize={9.5} fill={MUTED}>
          {m}
        </text>
      ))}
      <text x={R} y={B + 24} textAnchor="end" fontSize={10} fill={MUTED}>
        t (min)
      </text>
      {marks.map(([T, label]) => (
        <g key={label}>
          <line
            x1={L}
            y1={Y(T)}
            x2={R}
            y2={Y(T)}
            stroke={INK}
            strokeDasharray="4 3"
            opacity={0.6}
          />
          <HaloText
            x={R - 2}
            y={Y(T) - 4}
            textAnchor="end"
            fontSize={10}
            fill={INK}
            fontWeight={600}
          >
            {label}
          </HaloText>
        </g>
      ))}
      <polyline
        points={line.map(([time, T]) => `${X(time)},${Y(T)}`).join(' ')}
        fill="none"
        stroke={HOT}
        strokeWidth={2.4}
        strokeLinejoin="round"
      />
      <circle cx={X(t)} cy={Y(st.T)} r={4.5} fill={HOT} stroke="white" strokeWidth={1.5} />
      <Particles s={s} T={st.T} counts={counts} tau={(t / D) * 12} />
      <Thermometer T={st.T} range={s.range} />
    </svg>
  );
}

function StatesView({ rank }: { rank: number | null }) {
  const [id, setId] = useState('water');
  const s = substanceById(id);
  const [dir, setDir] = useState<Direction>(s.usual);
  const D = duration(s);
  const player = usePlayer(D, 12, `${id}-${dir}`);
  const st = stateAt(s, dir, player.t);
  const ch = changeOf(s, st, dir);
  const rows: Array<[string, string]> = [
    ['Température', tempText(st.T)],
    ['État', ch ? `${stateText(st)} (${ch})` : stateText(st)],
    ['Masse', `${MASS} g`],
    ['Volume', volumeText(volumeOf(s, st))],
  ];
  if (energetic(rank))
    rows.push([
      `Énergie ${dir === 'heat' ? 'reçue' : 'cédée'} Q = P × t`,
      `${fr((s.power * player.t) / 1000, 1)} kJ`,
    ]);
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Espèce chauffée"
        value={id}
        options={SUBSTANCES.map((x) => ({ value: x.id, label: x.label }))}
        onChange={(v) => {
          setId(v);
          setDir(substanceById(v).usual);
        }}
      />
      <Segmented
        ariaLabel="Sens"
        value={dir}
        options={[
          { value: 'heat', label: 'Chauffer' },
          { value: 'cool', label: 'Refroidir' },
        ]}
        onChange={setDir}
      />
      <StatesFigure s={s} dir={dir} t={player.t} />
      <PlayBar player={player} T={D} format={minutes} />
      <Readout rows={rows} />
      <Lines lines={stateLines(s, dir, player.t, rank)} />
    </div>
  );
}

/* ---------- masse volumique ---------- */

function DensityFigure({ matIdx, liqId, V }: { matIdx: number; liqId: string; V: number }) {
  const mat = MATERIALS[matIdx]!;
  const liq = LIQUIDS.find((l) => l.id === liqId)!;
  const m = mat.rho * V;
  const b = buoyancy(mat.rho, liq.rho);
  // bécher
  const bx0 = 16;
  const bx1 = 186;
  const surface = 62;
  const floor = 186;
  const side = 11 * Math.cbrt(V);
  const cx = (bx0 + bx1) / 2;
  const top =
    b === 'floats'
      ? surface - (1 - immersed(mat.rho, liq.rho)) * side
      : b === 'sinks'
        ? floor - side - 1
        : (surface + floor) / 2 - side / 2;
  // graphique m(V)
  const gL = 52;
  const gR = W - 14;
  const gT = 222;
  const gB = 318;
  const mMax = Math.max(mat.rho, liq.rho) * 100;
  const GX = (v: number) => gL + (v / 100) * (gR - gL);
  const GY = (mm: number) => gB - (mm / mMax) * (gB - gT);
  return (
    <svg
      viewBox={`0 0 ${W} 338`}
      className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
      role="img"
      aria-label={`Cube de ${mat.name}, ${fr(V, 0)} cm³, ${fr(m, 1)} g, qui ${b === 'floats' ? 'flotte' : b === 'sinks' ? 'coule' : 'reste entre deux eaux'} dans ${liq.the}`}
      data-testid="mt-density"
    >
      <rect
        x={bx0 + 2}
        y={surface}
        width={bx1 - bx0 - 4}
        height={floor - surface}
        fill={liq.color}
        opacity={0.35}
      />
      <path
        d={`M ${bx0 - 4} 22 L ${bx0} 28 L ${bx0} ${floor + 2} L ${bx1} ${floor + 2} L ${bx1} 28 L ${bx1 + 4} 22`}
        fill="none"
        stroke={INK}
        strokeWidth={2}
      />
      <line x1={bx0} y1={surface} x2={bx1} y2={surface} stroke={liq.color} strokeWidth={2} />
      <rect
        x={cx - side / 2}
        y={top}
        width={side}
        height={side}
        fill={mat.color}
        stroke={INK}
        strokeWidth={1.2}
        data-testid="mt-cube"
      />
      <HaloText x={bx0 + 6} y={floor - 6} fontSize={10} fill={INK}>
        {liq.label}
      </HaloText>
      <HaloText x={cx} y={14} textAnchor="middle" fontSize={11} fontWeight={700} fill={INK}>
        {b === 'floats' ? 'flotte' : b === 'sinks' ? 'coule' : 'entre deux eaux'}
      </HaloText>
      {/* balance */}
      <rect
        x={206}
        y={146}
        width={120}
        height={40}
        rx={8}
        fill="var(--color-ink-100)"
        stroke={INK}
      />
      <rect x={220} y={136} width={92} height={8} rx={3} fill="var(--color-ink-300)" stroke={INK} />
      <rect x={218} y={156} width={96} height={22} rx={4} fill="#1f2937" />
      <text
        x={308}
        y={172}
        textAnchor="end"
        fontSize={13}
        fontWeight={700}
        fill="#86efac"
        fontFamily="monospace"
      >
        {`${fr(m, 1)} g`}
      </text>
      <rect
        x={266 - Math.min(side, 40) / 2}
        y={136 - Math.min(side, 40)}
        width={Math.min(side, 40)}
        height={Math.min(side, 40)}
        fill={mat.color}
        stroke={INK}
      />
      <text x={266} y={200} textAnchor="middle" fontSize={10} fill={MUTED}>
        {`${fr(V, 0)} cm³ de ${mat.name}`}
      </text>
      {/* m = ρ × V */}
      <line x1={gL} y1={gB} x2={gR} y2={gB} stroke="var(--color-ink-300)" />
      <line x1={gL} y1={gT} x2={gL} y2={gB} stroke="var(--color-ink-300)" />
      <text x={gL - 5} y={gT + 4} textAnchor="end" fontSize={9.5} fill={MUTED}>
        {fr(mMax, 0)}
      </text>
      <text x={gL - 5} y={gB} textAnchor="end" fontSize={9.5} fill={MUTED}>
        0
      </text>
      <text x={gL - 5} y={gT - 10} textAnchor="end" fontSize={10} fontWeight={700} fill={INK}>
        m (g)
      </text>
      <text x={gR} y={gB + 14} textAnchor="end" fontSize={10} fill={MUTED}>
        V (cm³) → 100
      </text>
      <line
        x1={GX(0)}
        y1={GY(0)}
        x2={GX(100)}
        y2={GY(liq.rho * 100)}
        stroke={liq.color}
        strokeWidth={2}
        strokeDasharray="5 4"
      />
      <line
        x1={GX(0)}
        y1={GY(0)}
        x2={GX(100)}
        y2={GY(mat.rho * 100)}
        stroke={INK}
        strokeWidth={2}
      />
      <circle cx={GX(V)} cy={GY(m)} r={5} fill={mat.color} stroke={INK} strokeWidth={1.5} />
      <HaloText x={gL + 6} y={gT + 10} fontSize={10} fill={INK}>
        {`${mat.name} (trait plein) · ${liq.name} (tirets)`}
      </HaloText>
    </svg>
  );
}

function DensityView({ rank }: { rank: number | null }) {
  const [matIdx, setMatIdx] = useState(1);
  const [liqId, setLiqId] = useState('water');
  const [V, setV] = useState(20);
  const mat = MATERIALS[matIdx]!;
  const liq = LIQUIDS.find((l) => l.id === liqId)!;
  const b = buoyancy(mat.rho, liq.rho);
  const rows: Array<[string, string]> = [
    ['Masse volumique ρ', rhoText(mat.rho)],
    ['Volume V', `${fr(V, 0)} cm³`],
    ['Masse m = ρ × V', `${fr(mat.rho * V, 1)} g`],
  ];
  if (rank === null || rank >= 3) rows.push(['ρ en kg/m³', `${fr(mat.rho * 1000, 0)} kg/m³`]);
  rows.push([
    `Dans ${liq.the} (${rhoText(liq.rho)})`,
    b === 'floats' ? 'flotte' : b === 'sinks' ? 'coule' : 'entre deux eaux',
  ]);
  return (
    <div className="space-y-4">
      <DensityFigure matIdx={matIdx} liqId={liqId} V={V} />
      <Stepper
        label="Matériau du cube"
        value={matIdx}
        min={0}
        max={MATERIALS.length - 1}
        onChange={setMatIdx}
        format={(i) => MATERIALS[i]!.label}
      />
      <Segmented
        ariaLabel="Liquide"
        value={liqId}
        options={LIQUIDS.map((l) => ({ value: l.id, label: l.label }))}
        onChange={setLiqId}
      />
      <ParamSlider
        name="Volume du cube"
        ariaLabel="Volume du cube"
        value={V}
        min={1}
        max={100}
        step={1}
        onChange={setV}
        format={(v) => frenchSpaces(`${fr(v, 0)} cm³`)}
      />
      <Readout rows={rows} />
      <Lines lines={densityLines(mat, liq, V, rank)} />
    </div>
  );
}

/* ---------- dissolution ---------- */

const GRAINS = Array.from({ length: 60 }, (_, i) => {
  // positions fixes dans l'eau du bécher
  const a = (i * 7919) % 997;
  const b = (i * 104729) % 991;
  return [30 + (a / 997) * 150, 76 + (b / 991) * 98] as const;
});

function DissolveFigure({ soluteId, added, T }: { soluteId: string; added: number; T: number }) {
  const s = SOLUTES.find((x) => x.id === soluteId)!;
  const d = dissolve(s, added, T);
  const per = s.max / 50; // une particule dessinée pour `per` grammes dissous
  const nDots = Math.min(GRAINS.length, Math.round(d.dissolved / per));
  const heapH = d.deposit > 0.05 ? Math.min(40, 6 + (d.deposit / s.max) * 90) : 0;
  const floor = 184;
  return (
    <svg
      viewBox={`0 0 ${W} 214`}
      className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
      role="img"
      aria-label={`${fr(added, 0)} g de ${s.label.toLowerCase()} dans ${WATER_ML} mL d’eau à ${fr(T, 0)} °C : ${fr(d.dissolved, 0)} g dissous, ${fr(d.deposit, 0)} g au fond`}
      data-testid="mt-dissolve"
    >
      <rect x={22} y={66} width={166} height={floor - 66} fill="#60a5fa" opacity={0.22} />
      <path
        d={`M 16 22 L 20 28 L 20 ${floor + 2} L 190 ${floor + 2} L 190 28 L 194 22`}
        fill="none"
        stroke={INK}
        strokeWidth={2}
      />
      {GRAINS.slice(0, nDots).map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={2.2} fill={SALT} data-testid="mt-dissolved" />
      ))}
      {heapH > 0.5 && (
        <path
          d={`M 50 ${floor} Q 105 ${floor - 2 * heapH} 160 ${floor} Z`}
          fill="white"
          stroke={INK}
          strokeWidth={1}
          data-testid="mt-deposit"
        />
      )}
      <HaloText x={105} y={14} textAnchor="middle" fontSize={11} fontWeight={700} fill={INK}>
        {d.saturated ? 'solution saturée' : added > 0 ? 'tout est dissous' : 'eau pure'}
      </HaloText>
      {/* balance */}
      <rect
        x={206}
        y={146}
        width={120}
        height={40}
        rx={8}
        fill="var(--color-ink-100)"
        stroke={INK}
      />
      <rect x={218} y={156} width={96} height={22} rx={4} fill="#1f2937" />
      <text
        x={308}
        y={172}
        textAnchor="end"
        fontSize={13}
        fontWeight={700}
        fill="#86efac"
        fontFamily="monospace"
      >
        {`${fr(WATER_ML + added, 0)} g`}
      </text>
      <text x={266} y={202} textAnchor="middle" fontSize={10} fill={MUTED}>
        balance (bécher taré)
      </text>
    </svg>
  );
}

function DissolveView({ rank }: { rank: number | null }) {
  const [soluteId, setSoluteId] = useState('salt');
  const [added, setAdded] = useState(20);
  const [T, setT] = useState(20);
  const s = SOLUTES.find((x) => x.id === soluteId)!;
  const d = dissolve(s, added, T);
  const rows: Array<[string, string]> = [
    ['Eau', `${WATER_ML} mL (${WATER_ML} g)`],
    [`Solubilité à ${fr(T, 0)} °C`, `${fr(d.solubility, 0)} g pour ${WATER_ML} mL`],
    ['Dissous', `${fr(d.dissolved, 0)} g`],
    ['Au fond', `${fr(d.deposit, 0)} g`],
    ['Masse totale', `${fr(WATER_ML + added, 0)} g`],
  ];
  if (rank === null || rank >= 5) {
    rows.push(['Volume de la solution', `${fr(d.volume, 0)} mL`]);
    rows.push(['C_m = m(dissous) ÷ V', `${fr(d.cm, 0)} g/L`]);
  }
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on dissout"
        value={soluteId}
        options={SOLUTES.map((x) => ({ value: x.id, label: x.label }))}
        onChange={(v) => {
          setSoluteId(v);
          setAdded(v === 'salt' ? 20 : 160);
        }}
      />
      <DissolveFigure soluteId={soluteId} added={added} T={T} />
      <Stepper
        label={`${s.label} ajouté`}
        value={added}
        min={0}
        max={s.max}
        step={s.step}
        onChange={setAdded}
        format={(v) => frenchSpaces(`${fr(v, 0)} g`)}
      />
      <ParamSlider
        name="Température de l’eau"
        ariaLabel="Température de l’eau"
        value={T}
        min={0}
        max={100}
        step={5}
        onChange={setT}
        format={(v) => frenchSpaces(`${fr(v, 0)} °C`)}
      />
      <Readout rows={rows} />
      <Lines lines={dissolveLines(s, added, T, rank)} />
    </div>
  );
}

function Explore({ rank }: { rank: number | null }) {
  const parts = partsFor(rank);
  const [part, setPart] = useState<Part>(parts[0]!.id);
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie"
        value={part}
        options={parts.map((p) => ({ value: p.id, label: p.label }))}
        onChange={setPart}
      />
      {part === 'states' && <StatesView rank={rank} />}
      {part === 'density' && <DensityView rank={rank} />}
      {part === 'dissolve' && <DissolveView rank={rank} />}
    </div>
  );
}

export function MatterTool({
  level = null,
  seed,
}: {
  /** niveau : parties et notions selon la classe (6e → 2nde) */
  level?: string | null;
  /** graine du défi (tests) */
  seed?: number;
}) {
  const rank = schoolLevelRank(level);
  const [mode, setMode] = useState<'explore' | 'quiz'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore rank={rank} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <McqChallenge kinds={quizKinds(rank)} make={makeQuiz} seed={seed} testId="mt" rich />
      </div>
    </div>
  );
}
